import iCollection from "../models/iCollection";

export enum SortHotelsBy {
  RECOMMENDED = "recommended",
  DISTANCE_TO_CC = "distance_to_cc",
  REVIEWS = "reviews_score",
  PRICE = "price",
}

export const bookingEngines = [
  "none",
  "AmberGris",
  "Amenitiz",
  "AvaiBook",
  "Avvio",
  "Bed-And-Breakfast",
  "Beddy",
  "Bedzzle",
  "BeMyGuest",
  "Best Western Italia",
  "Blastness",
  "Bnext",
  "BookAssist",
  "BookCore",
  "Booking Designer",
  "BookingExpert",
  "Cedyn",
  "Cloudbeds",
  "CultBooking",
  "Direct Book",
  "D-edge",
  "Dhynet",
  "EasyConsulting",
  "Ericsoft",
  "Ermes Hotels",
  "Guestcentric",
  "Hotel BB",
  "Hotel Cinque Stelle",
  "Hotel in Cloud",
  "Hotelmanagement",
  "Hotelrunner",
  "Hotetec",
  "Hotusa",
  "HyperGuest",
  "iHotelier",
  "InReception",
  "Iperbooking",
  "Kross Travel",
  "Little Hotelier",
  "Mews",
  "Mirai",
  "MyForecast",
  "MyGuestCare",
  "Neobookings",
  "Net Affinity",
  "Nozio",
  "Octorate",
  "Paraty Tech",
  "Parity Rate",
  "Passpartout",
  "Profitroom",
  "Quovai",
  "RateGain",
  "RateTiger",
  "Reservation in Cloud",
  "Roiback",
  "RoomCloud",
  "Scidoo",
  "Secure Hotel Booking",
  "Simplebooking",
  "Simplotel",
  "SiteMinder",
  "Slope",
  "Synxis",
  "Sysdat",
  "TravelClick",
  "Vertical Booking",
  "WebHotelier",
  "Witbooking",
  "WuBook",
  "Zak/ WoDoo",
  "other",
] as const;

export type bookingEngine = (typeof bookingEngines)[number];

type LatLng = {
  lat: number;
  lng: number;
};

type HotelProperty = {
  accuratePropertyClass: number;
  countryCode: string;
  name: string;
  isPreferred: boolean;
  images: string[];
  currency: string;
  ufi: number;
  propertyClass: number;
  qualityClass: number;
  distance_to_cc: number;
  address: string;
  city: string;
  country: string;
  currency_code: string;
  spoken_languages: string[];
  facilities: { id: number; instances: Array<{ title: string }> }[];
  checkin: { fromTime: string; untilTime: string };
  checkout: { fromTime: string; untilTime: string };
  description: Array<{ languagecode: string; description: string }>;
};

export type FilterType = keyof Filters;
export type Filters = {
  minPrice?: number;
  maxPrice?: number;
  certifiedOnly?: boolean;
  isLoyaltyEnabled?: boolean;
  includesBenefits?: boolean;
  onlyHotels?: boolean;
  minReviewsScore?: string;
  distanceToCC?: number;
  stars?: number[];
  propertyTypes?: string[];
  facilities?: string[];
};

export interface SearchParameters {
  // Booking parameters
  destinationLabel?: string;
  arrivalDate?: string;
  departureDate?: string;
  adults: number;
  childrenAge: number[];
  filters?: Filters;

  // Search parameters
  searchType: "city" | "box" | "tag";
  searchLocationType?: "hotel" | "known_location" | "nearby_location";
  placeId?: string;
  collectionId?: string;
  boxCoordinates?: {
    northeast: LatLng;
    southwest: LatLng;
  };

  // Sorting and pagination
  sortBy?: SortHotelsBy;
  orderDirection?: "asc" | "desc";
  skip?: number;
  size?: number;

  // Display options
  language?: string;
  currency?: string;

  tag?: "city" | "lake" | "mountain" | "sea" | "wellness" | "countryside";
}

export type KeyValuePair = {
  key: FilterType;
  value: string | number | boolean;
};

type MongoDbLocation = {
  type: "Point";
  coordinates: [number, number]; // [lng, lat]
};

export type HotelPrice = {
  _id: string;
  price: number;
  currency: string;
  mode: string; //"bookingcom" | "hyperguest";
  // null indicates API failures
  available: boolean | null;
};

export type Hotel = {
  _id: iCollection["_id"];
  index: number;
  name: iCollection["name"];
  website: iCollection["website"];
  stars: iCollection["stars"];
  slug: iCollection["slug"];
  reviewScore?: number;
  reviewCount?: number;
  images: HotelProperty["images"];
  distanceToCc: HotelProperty["distance_to_cc"];
  advantagesOnDirectWebsite: iCollection["preData"]["advantagesOnDirectWebsite"];
  deltaPriceBookingCom: iCollection["preData"]["deltaPriceBookingCom"];
  deltaPriceHotelsCom: iCollection["preData"]["deltaPriceHotelsCom"];
  isLoyaltyEnabled: iCollection["isLoyaltyEnabled"];
  isLoyaltyEnabledAdmin: iCollection["isLoyaltyEnabledAdmin"];
  isOnboardingComplete: iCollection["isOnboardingComplete"];
  isActive: iCollection["isActive"];
  loyatyDiscountPercentage: iCollection["loyatyDiscountPercentage"];
  meanPrice: number;
  mongoDbLocation: MongoDbLocation;
  hotelsId?: string;
  bookingId?: string;
  city: string;
  address: string;
  availability?: wsAvailability;
  phone: string;
};

export type SearchHotelReturnType = {
  hotels: Hotel[];
  nearHotels: Hotel[];
  allResults: boolean;
};

export type SearchNearbyReturnType = {
  nearHotels: Hotel[];
};

export type priceProviders = {
  bookingEngine?: {
    type: (typeof bookingEngines)[number];
    params: any;
  };
  hotelscom?: { id: string };
  bookingcom?: { id: string };
};

export type getAvailabilityPayload = {
  id: string;
  priceProviders: priceProviders;
};

export type AvailabilityInfo = {
  value: number;
  currency: string;
  provider?: bookingEngine | "bookingcom" | "hotelscom";
  available: boolean;
};

export type wsAvailability = {
  _id: string;
  availabilityInfo: AvailabilityInfo;
};

export type HotelDetails = {
  _id: iCollection["_id"];
  name: iCollection["name"];
  slug: iCollection["slug"];
  isActive: iCollection["isActive"];
  website: iCollection["website"];
  stars: iCollection["stars"];

  phone: iCollection["phone"];
  email: iCollection["email"];
  locationType: iCollection["locationType"];
  cin: iCollection["cin"];
  cir: iCollection["cir"];

  advantagesOnDirectWebsite: iCollection["preData"]["advantagesOnDirectWebsite"];
  deltaPriceBookingCom: iCollection["preData"]["deltaPriceBookingCom"];
  deltaPriceHotelsCom: iCollection["preData"]["deltaPriceHotelsCom"];
  reviewData: iCollection["directsearchData"]["reviewData"];
  property: iCollection["directsearchData"]["property"];
  address: string;
  city: string;
  mongoDbLocation: iCollection["directsearchData"]["mongoDbLocation"];
  meanPrice: iCollection["directsearchData"]["meanPrice"];
  isLoyaltyEnabled: iCollection["isLoyaltyEnabled"];
  isLoyaltyEnabledAdmin: iCollection["isLoyaltyEnabledAdmin"];
  isOnboardingComplete: iCollection["isOnboardingComplete"];
  loyatyDiscountPercentage: iCollection["loyatyDiscountPercentage"];
  hotelsId?: string;
  bookingId?: string;
  availability?: wsAvailability;
  priceProviders?: iCollection["priceProviders"];
};

export enum SocketEvent {
  SEARCH = "search",
  DISCONNECT = "disconnect",
  HOTEL = "hotel",
  SEARCHING_NEAR_HOTELS = "searching_near_hotels",
  NEAR_HOTEL = "near_hotel",
  UAVAILABLE = "unavailable",
  DONE = "done",
  VIEWPORT = "viewport",
  ERROR = "error",
}

export function arraifyFilters(filters?: Filters): KeyValuePair[] {
  const filterStrings: KeyValuePair[] = [];
  if (!filters) return filterStrings;

  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      filterStrings.push(
        ...value.map((v) => ({ key: key as FilterType, value: v })),
      );
    } else if (value !== undefined) {
      filterStrings.push({ key: key as FilterType, value: value });
    }
  }

  return filterStrings;
}

export function unarraifyFilters(filters: KeyValuePair[]): Filters {
  let filterObj: Filters = {};

  for (const filter of filters) {
    const { key, value } = filter;
    if (["stars", "propertyTypes", "facilities"].includes(key)) {
      if (!filterObj[key]) {
        filterObj = { ...filterObj, [key]: [value] };
      } else {
        // @ts-ignore
        filterObj[key].push(value);
      }
    } else if (value !== undefined) {
      filterObj = { ...filterObj, [key]: value };
    }
  }

  return filterObj;
}
